import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useState } from "react";
import { CalendarClock, TrendingUp, Users, DollarSign, Award, Radar, Check, X, Clock, ArrowUpRight, AlertTriangle, GraduationCap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Loading, Money } from "../components/shared";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { WeeklyDigestCard } from "../components/academy/WeeklyDigest";

const PRIORITY = {
  high: "bg-rose-100 text-rose-700 border-rose-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
};

const NBA_ROUTE = { diagnostic: "/diagnostic", opportunity: "/pipeline", proposal: "/proposals", engagement: "/engagements", academy: "/academy" };

export default function ControlTower() {
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [snoozeFor, setSnoozeFor] = useState(null);
  const [reason, setReason] = useState("");

  const { data: ct, isLoading } = useQuery({ queryKey: ["control-tower"], queryFn: async () => (await api.get("/dashboard/control-tower")).data });
  const { data: nba } = useQuery({ queryKey: ["nba"], queryFn: async () => (await api.get("/dashboard/next-best-actions")).data });

  const act = useMutation({
    mutationFn: (body) => api.post("/dashboard/next-best-actions/act", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["nba"] }); },
  });

  if (isLoading || !ct) return <Loading />;
  const c = ct.cards;

  const cards = [
    { key: "today", icon: CalendarClock, label: "Today", value: c.today.due_today, sub: `${c.today.open_tasks} open task(s)`, tint: "sky", to: "/pipeline" },
    { key: "pipeline", icon: TrendingUp, label: "Pipeline", value: <Money value={c.pipeline.value} />, sub: `${c.pipeline.open_count} open opportunities`, tint: "emerald", to: "/pipeline" },
    { key: "clients", icon: Users, label: "Clients", value: c.clients.active_engagements, sub: `${c.clients.total_engagements} engagements`, tint: "indigo", to: "/engagements" },
    { key: "revenue", icon: DollarSign, label: "Revenue", value: <Money value={c.revenue.mrr} />, sub: `MRR · ${c.revenue.won_count} won`, tint: "teal", to: "/pricing" },
    { key: "development", icon: Award, label: "Development", value: c.development.cpd_credits ?? 0, sub: `${c.development.courses_completed ?? 0} courses · ${c.development.certifications} certs`, tint: "violet", to: "/academy" },
    { key: "network", icon: Radar, label: "Network Opportunities", value: c.network.leads, sub: `${c.network.new_this_week} new this week`, tint: "amber", to: "/finder" },
  ];

  const tintMap = {
    sky: "bg-sky-50 text-sky-600", emerald: "bg-emerald-50 text-emerald-600", indigo: "bg-indigo-50 text-indigo-600",
    teal: "bg-teal-50 text-teal-600", violet: "bg-violet-50 text-violet-600", amber: "bg-amber-50 text-amber-600",
  };

  return (
    <div>
      <PageHeader eyebrow={`Welcome back, ${(user?.name || "").split(" ")[0]}`} title="Control Tower"
        description="Your practice at a glance. Six views, one command centre — with rule-based Next Best Actions you stay in control of." />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map((card, i) => (
          <motion.button key={card.key} onClick={() => nav(card.to)} data-testid={`ct-card-${card.key}`}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="text-left bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all p-6 group">
            <div className="flex items-start justify-between">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tintMap[card.tint]}`}><card.icon className="w-5 h-5" /></div>
              <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 transition-colors" />
            </div>
            <div className="mt-4 font-display text-3xl font-bold text-slate-900">{card.value}</div>
            <div className="text-sm font-medium text-slate-700 mt-1">{card.label}</div>
            <div className="text-xs text-slate-400 mt-0.5">{card.sub}</div>
          </motion.button>
        ))}
      </div>

      {/* pipeline mini + health + weekly learning digest */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-8">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-slate-900">Pipeline by stage</h3>
            <Button variant="ghost" size="sm" onClick={() => nav("/pipeline")} className="text-emerald-600">Open pipeline</Button>
          </div>
          <div className="space-y-2.5">
            {Object.entries(c.pipeline.stages).map(([stage, count]) => (
              <div key={stage} className="flex items-center gap-3">
                <div className="w-24 text-xs text-slate-500 font-medium">{stage}</div>
                <div className="flex-1 h-6 bg-slate-100 rounded-lg overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg transition-all" style={{ width: `${Math.min(100, count * 22)}%` }} />
                </div>
                <div className="w-6 text-right text-sm font-semibold text-slate-700">{count}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
          <h3 className="font-display font-semibold text-slate-900 mb-4">Delivery health</h3>
          {["green", "amber", "red"].map((h) => (
            <div key={h} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
              <div className="flex items-center gap-2.5">
                <span className={`w-2.5 h-2.5 rounded-full ${h === "green" ? "bg-emerald-500" : h === "amber" ? "bg-amber-500" : "bg-rose-500"}`} />
                <span className="text-sm text-slate-600 capitalize">{h === "green" ? "On track" : h === "amber" ? "Watch" : "At risk"}</span>
              </div>
              <span className="font-semibold text-slate-800">{c.clients.health[h]}</span>
            </div>
          ))}
        </div>
        <div className="lg:col-span-3"><WeeklyDigestCard /></div>
      </div>

      {/* Next Best Actions */}
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="font-display text-xl font-bold text-slate-900">Next Best Actions</h2>
          <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">rule-based</span>
        </div>
        <div className="space-y-3">
          {(nba?.actions || []).length === 0 && (
            <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
              You're all caught up. New actions will appear as your pipeline and engagements evolve.
            </div>
          )}
          {(nba?.actions || []).map((a, i) => (
            <motion.div key={a.key} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
              data-testid="nba-item" className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${a.entity_type === "academy" ? "bg-emerald-50 text-emerald-600" : a.priority === "high" ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-600"}`}>
                {a.entity_type === "academy" ? <GraduationCap className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900 text-sm">{a.title}</span>
                  <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-full border ${PRIORITY[a.priority]}`}>{a.priority}</span>
                </div>
                <p className="text-sm text-slate-500 mt-1 leading-relaxed">{a.reason}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button size="sm" onClick={() => nav(NBA_ROUTE[a.entity_type] || "/")} data-testid="nba-open" className="bg-emerald-600 hover:bg-emerald-700 h-8">Open</Button>
                <Button size="sm" variant="outline" onClick={() => act.mutate({ key: a.key, action: "complete" })} data-testid="nba-complete" className="h-8 w-8 p-0"><Check className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => setSnoozeFor(a)} data-testid="nba-snooze" className="h-8 w-8 p-0"><Clock className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => act.mutate({ key: a.key, action: "dismiss" })} data-testid="nba-dismiss" className="h-8 w-8 p-0"><X className="w-4 h-4" /></Button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <Dialog open={!!snoozeFor} onOpenChange={(o) => { if (!o) { setSnoozeFor(null); setReason(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Snooze action</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">"{snoozeFor?.title}" will be hidden for 7 days. Add a reason for your records.</p>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="snooze-reason" placeholder="Why are you snoozing this?" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setSnoozeFor(null); setReason(""); }}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="snooze-confirm"
              onClick={() => { act.mutate({ key: snoozeFor.key, action: "snooze", reason, snooze_days: 7 }); toast.success("Snoozed for 7 days"); setSnoozeFor(null); setReason(""); }}>
              Snooze 7 days
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
